// Sync Responsiveness: Member events are dispatched in batches of MEMBER_BATCH_SIZE (default 10)
// with event-loop yields (setTimeout(0)) between batches. This prevents the main thread from
// blocking during sync when hundreds of individuals arrive per page. The next HTTP page request
// is started before batch dispatch so network I/O overlaps with event processing.
// See docs/sync-responsiveness.md for details.

// TODO abstract the basic interaction with the queue if possible...

import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'

import { CACHE_FORMAT_VERSION } from '@/rec-cache/rec-cache-format'
import {
  IndividualRefreshEvent,
  IndividualDeleteEvent,
  SyncProgressEvent,
  SyncSkippedIdsEvent,
  CacheSyncCompleteEvent,
} from '@/rec-cache/rec-cache-events'
import { WobClientBase } from '@/wob-client/wob-client-base'

// TODO temporary solution adding the WoB ontology to the context.
// Preferably this should be based on reading the models from ADT
import recContext from './rec-context.json'
import woboContext from './wobo-context.json'
// The rec context will override the wobo context in case of overlaps.
const context = Object.assign({}, woboContext, recContext)

// TODO temporary solution to support creation with components...
const { ARCHITECTURE_MODELS } = require('./adt-architectures')
console.log('ADT ARCH MODELS', ARCHITECTURE_MODELS)

const { adtParser, toDTDL } = require('./adt-parser')

// Fast 53-bit hash for change-detection signatures (non-cryptographic).
// Produces a number in JS safe-integer range with excellent distribution.
// Collision probability with 9 000 entries ≈ 9000² / 2^54 ≈ 0.0000005%.
function cyrb53(str, seed = 0) {
  let h1 = 0xdeadbeef ^ seed
  let h2 = 0x41c6ce57 ^ seed
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
  h1 ^= Math.imul(h2 ^ (h2 >>> 16), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
  h2 ^= Math.imul(h1 ^ (h1 >>> 16), 3266489909)
  return 4294967296 * (2097151 & h2) + (h1 >>> 0)
}

// Transaction journal for change-type operations.
// Persisted to localStorage so recovery is possible after app crash.
const CHANGE_TYPE_JOURNAL_KEY = 'changeTypeTransaction'

// localStorage key for the etagMap cache. Keyed on the shared persisted-cache
// format version, NOT the app version — see rec-cache-format.js. The entries
// describe what IndexedDB holds, so the two must invalidate together; keying
// on the app version orphaned the map on every deployment and cost each user
// a full re-parse of the graph on their next load.
//
// Uses localStorage rather than sessionStorage so the map survives across tabs,
// avoiding expensive full re-syncs when IDB already has the data.
const ETAG_MAP_STORAGE_KEY = `etagMap_v${CACHE_FORMAT_VERSION}`

// Retry helper for rate-limited requests (429 errors).
// Uses exponential backoff: 1s, 2s, 4s, 8s
async function withRetry(fn, maxRetries = 4) {
  let lastError
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error
      const status = error?.response?.status
      if (status === 429 && attempt < maxRetries) {
        const delay = Math.pow(2, attempt) * 1000 // 1s, 2s, 4s, 8s
        console.log(
          `wob-client.adt-client: 429 rate limited, retrying in ${delay}ms (attempt ${
            attempt + 1
          }/${maxRetries})`
        )
        await new Promise((resolve) => setTimeout(resolve, delay))
      } else {
        throw error
      }
    }
  }
  throw lastError
}

// Sequential execution helper to avoid rate limiting.
// Executes an array of async functions one at a time.
async function sequential(fns) {
  const results = []
  for (const fn of fns) {
    results.push(await fn())
  }
  return results
}

class ChangeTypeJournal {
  static read() {
    try {
      const raw = localStorage.getItem(CHANGE_TYPE_JOURNAL_KEY)
      return raw ? JSON.parse(raw) : null
    } catch (error) {
      console.error('ChangeTypeJournal: Failed to read journal', error)
      return null
    }
  }

  static write(journal) {
    try {
      journal.updatedAt = new Date().toISOString()
      localStorage.setItem(CHANGE_TYPE_JOURNAL_KEY, JSON.stringify(journal))
      console.log('ChangeTypeJournal: Written', journal.state, journal)
    } catch (error) {
      console.error('ChangeTypeJournal: Failed to write journal', error)
      throw error
    }
  }

  static clear() {
    localStorage.removeItem(CHANGE_TYPE_JOURNAL_KEY)
    console.log('ChangeTypeJournal: Cleared')
  }
}
// TODO refactor parameters into one object... basically use the Axios config
class RequestConfig {
  constructor(url, headers, data, method = 'get') {
    this.url = url
    this.headers = headers
    this.data = data
    this.method = method
  }
}

class Request {
  constructor(config, id = null) {
    this.id = id || Math.random().toString(36).substring(2, 10)
    this.config = config || {}
  }
}

class AdtClient extends WobClientBase {
  // Constructs a proxy URL with the inner ADT URL properly URI-encoded.
  // Without encoding, query parameters in the inner URL (e.g. continuationToken, api-version)
  // get parsed as parameters of the proxy URL, causing 400 errors on paginated requests.
  static proxyUrl(adtUrl) {
    return (
      'https://webofbuildings.com/adtproxy?url=' + encodeURIComponent(adtUrl)
    )
  }

  constructor({
    // ADT query limits constrain the page size:
    // - IN-clause max: 100 items (used for relationship queries per page)
    // - Query string max: 8000 characters
    // See https://aka.ms/adtv2query
    pageSize = 100,
    origin = '',
    token = null,
    isAuthenticated = null,
    idFactory = uuidv4,
  } = {}) {
    super()
    console.log('wob-client.wob-client.constructor')

    // Set the auth token.
    this.token = token
    // TODO is autheticated may potentially be a getter based on token...
    this.isAuthenticated = isAuthenticated

    this.idFactory = idFactory

    // Define and initialize variables for forming the ADT API request.
    this.origin = origin
    this.queryFlags = '?api-version=2023-10-31'
    this.authHeader = token
      ? (this.authHeader = { Authorization: `Bearer ${this.token}` })
      : {}
    this.pageSizeHeader = { 'max-items-per-page': pageSize }

    // Initialize request queue.
    // NOTE: Currently only used by readAllRequest() for paginated queries.
    // All manipulation methods (create/update/delete) use axios directly.
    // If queue usage expands to include other operations, request-ID tracking
    // will be needed to monitor completion of specific request chains.
    this.queue = []
    this.abortReqId = null

    // Initialize the size of the collection.
    this.collectionSize = null
    this.isCountReceived = false // Track if we've received and dispatched the count
    this.actualIndividualsDispatched = 0 // Track actual individuals (twins + components)
    this.twinsLoaded = 0 // Track twins loaded for progress reporting (client-specific units)

    // Per-phase sync timing. Populated by readAllRequest() and reported
    // once the last batch of the cycle has been dispatched. Mirrors the
    // PCX client's instrumentation so the two backends are comparable.
    this._timing = null
    this._pendingBatches = 0
    this._syncCompleted = false

    // Initialize the web worker.
    this.worker = new Worker(new URL('./wob-client-worker.js', import.meta.url))

    // Initialize the cache holding individuals (twins) while relationships are read.
    this.pageCache = []

    // ETag change detection: skips parsing twins whose etag and relationship
    // signature are unchanged since the last sync.
    // Map<twinId, { twinEtag: string, relHash: number }>
    // relHash is a cyrb53 hash of the sorted relationship signature string.
    //
    // Persisted by the cache, in IndexedDB beside the individuals it describes
    // (see getChangeDetectionState / restoreChangeDetection). It used to live
    // in localStorage, which caps an origin at ~5MB: the map outgrew that
    // somewhere between ~31k and ~44k twins, the save began failing, and every
    // sync from then on replayed every twin added since the last save that
    // fitted. That key was also global to the origin while the cache is per
    // user; the IndexedDB copy has exactly the cache's scope. The legacy copy
    // is still read here so the first load after the move keeps its skips; it
    // is deleted once the cache has persisted a replacement.
    //
    // Every mutation below is in memory only. The cache's next save persists
    // it — createAsync/updateAsync await one immediately after an edit.
    this.etagMap = this._loadLegacyEtagMap()

    // Track twin IDs seen during current sync cycle for stale entry cleanup.
    this.syncSeenIds = null

    // Twin IDs skipped by the etagMap filter during the current sync cycle.
    // Dispatched as SyncSkippedIdsEvent before CacheSyncCompleteEvent so the
    // cache layer knows these twins still exist (just weren't re-parsed).
    this.syncSkippedIds = []

    // TODO consider building a cache of the read ADT twins and relationships to speed up manipulations.
    // Initialize the relationship id map. Maps relationship source+target+name -> id.
    // this.relationships = new Map()

    this.worker.onmessage = async (message) => {
      const { data: response } = message
      // The response is expanded JSON LD comprising array of graphs. The first element is the collection.
      const { id: requestId, data: adt } = response
      const { continuationToken, value: values } = adt

      // Remove finalized request from queue.
      this.queue = this.queue.filter((r) => r.id != requestId)

      // Check if return with twins or relationships.
      // For twins, first extract and create anonymous individuals for the components.
      // Save all individuals to the page cache.
      // Summarize all twin ids and initiate request cycle relationships with twin as source
      // For relationships, enhance the cached individuals with their relationships.
      // For each individual expand to JSON-LD and publish as update event.

      // console.log(
      //   'wob-client.adt-client.worker-on-message',
      //   values,
      //   continuationToken
      // )

      // A token change will ensure that ongoing paginated responses will not initiate new requests.
      // Nor will they return any values to the cache.
      if (requestId != this.abortReqId) {
        // Refresh token before processing to ensure fresh headers for any new requests
        await this.refreshToken()

        // Check if this is a COUNT query response
        if (values.length > 0 && values[0].COUNT !== undefined) {
          // COUNT query response - just log it, don't dispatch yet
          // ADT COUNT returns twin count only, not twins+components
          // We'll dispatch actual size when pagination completes
          this.collectionSize = values[0].COUNT
          this.isCountReceived = true
          console.log(
            `ADT: Received twin count: ${this.collectionSize} (components not included in COUNT)`
          )

          // Start next pending request cycle if queue is not empty
          const [first] = this.queue
          if (first) this.worker.postMessage(first)
          return
        }

        let individualsToBatch = null

        if (this.pageCache.length) {
          // Incoming response with relationships.
          // console.log(
          //   'wob-client.adt-client.worker-on-message.relationships',
          //   values
          // )
          // console.log(
          //   'wob-client.adt-client.worker-on-message.relationships.page-cache',
          //   this.pageCache
          // )

          // Add the relationships to the cached twins.
          values.forEach((r) => {
            // TODO use deconstruction instead...
            const sourceId = r['$sourceId']
            const targetId = r['$targetId']
            const relationshipName = r['$relationshipName']
            // const relationshipId = r['$relationshipId']

            // Update twin with outgoing relationships.
            const twin = this.pageCache.find((t) => t['$dtId'] == sourceId)
            if (twin) {
              if (twin[relationshipName]) twin[relationshipName].push(targetId)
              else twin[relationshipName] = [targetId]
            }
          })

          // The relationships of one twin page can span several response
          // pages. Until 0.9.10 the continuation token was dropped here, so
          // everything past the first response page was silently lost: on the
          // Fabege graph, 427 VAV boxes whose `includes` edges sat on systems
          // with ~1,600 of them ended up as roots. Change detection then
          // fingerprinted the truncated set, so later syncs never repaired it.
          //
          // Keep the twins in pageCache and fetch the rest first. unshift, not
          // push: the queue already holds the next twin page, which must not
          // arrive before this page is complete (pageCache is what tells a
          // relationship response from a twin response).
          if (continuationToken) {
            this.queue.unshift(
              new Request(
                this.continuationRequestConfigFactory(continuationToken, false)
              )
            )
            if (this._timing) this._timing.relContinuations++
            console.log(
              `ADT: relationships continue on another response page (${values.length} in this one)`
            )
          } else {
            // ETag filter: skip twins+relationships unchanged since last sync.
            const tFilterStart = performance.now()
            const metaKeys = ['$dtId', '$metadata', '$etag']
            const changedTwins = this.pageCache.filter((twin) => {
              const twinId = twin['$dtId']
              const twinEtag = twin['$etag']

              // Build sorted relationship signature and hash it.
              // After merge, relationship keys are the ONLY Array values on the twin.
              const relPairs = []
              for (const [key, value] of Object.entries(twin)) {
                if (metaKeys.includes(key)) continue
                if (Array.isArray(value)) {
                  value.forEach((targetId) =>
                    relPairs.push(`${key}:${targetId}`)
                  )
                }
              }
              const relHash = cyrb53(relPairs.sort().join('|'))

              const stored = this.etagMap.get(twinId)
              if (
                stored &&
                stored.twinEtag === twinEtag &&
                stored.relHash === relHash
              ) {
                this.syncSkippedIds.push(twinId)
                return false // unchanged — skip parsing
              }

              this.etagMap.set(twinId, { twinEtag, relHash })
              return true // changed or new — parse
            })

            if (this._timing) {
              this._timing.filter += performance.now() - tFilterStart
              this._timing.relPages++
            }

            console.log(
              `ADT: ETag filter — ${changedTwins.length} changed of ${this.pageCache.length} twins`
            )

            // Parse only changed twins into JSON-LD.
            // The ADT parser returns an array of expanded JSON-LD individuals.
            // That is the twin itself and its components, if any.
            const tParseStart = performance.now()
            const parserResults = await Promise.allSettled(
              changedTwins.map((p) => adtParser(p))
            )
            if (this._timing) {
              this._timing.parse += performance.now() - tParseStart
            }
            const jsonlds = parserResults
              .map((p) => p.value)
              .reduce((prev, curr) => prev.concat(curr), [])

            // Reset the page cache.
            this.pageCache = []

            // Flatten parsed individuals for batched dispatch.
            const individuals = jsonlds.reduce(
              (prev, curr) => prev.concat(curr),
              []
            )

            // Save for batched dispatch after starting next network request.
            individualsToBatch = individuals
          }
        } else {
          // Incoming response with twins.
          // TODO extract components...

          const idKey = '$dtId'

          // Track twin IDs seen during this sync for stale etagMap cleanup.
          if (!this.syncSeenIds) this.syncSeenIds = new Set()
          values.forEach((v) => this.syncSeenIds.add(v[idKey]))

          const idsStr = values.map((v) => `'${v[idKey]}'`).join(', ')

          // console.log(
          //   'wob-client.adt-client.worker-on-message.twins-ids',
          //   idsStr
          // )

          if (this._timing) this._timing.twinPages++

          // Save the page of twins to the cache.
          this.pageCache = values

          // Track progress: increment twins loaded and dispatch progress event
          this.twinsLoaded += values.length
          if (this.isCountReceived && this.collectionSize > 0) {
            console.log(
              `ADT: Progress - loaded ${this.twinsLoaded} of ${this.collectionSize} twins`
            )
            dispatchEvent(
              new CustomEvent(SyncProgressEvent, {
                detail: {
                  loaded: this.twinsLoaded,
                  total: this.collectionSize,
                },
              })
            )
          }

          // Add request for relationships to queue.
          // SELECT * FROM RELATIONSHIPS WHERE $sourceId IN ['realestate-01','building-01']
          // Its continuation pages, if any, are followed in the relationship
          // branch above before the page is processed.
          this.queue.push(
            new Request(
              this.queryRequestConfigFactory(
                'SELECT * FROM RELATIONSHIPS WHERE $sourceId IN [ ' +
                  idsStr +
                  ' ]',
                false
              )
            )
          )

          // Queue request if continuation token, reuse request id.
          if (continuationToken) {
            this.queue.push(
              new Request(
                this.continuationRequestConfigFactory(continuationToken),
                requestId
              )
            )
          }
        }

        // Check if pagination complete (no more requests) and dispatch sync complete
        // NOTE: This assumes only readAllRequest() uses the queue (verified: all manipulation
        // methods like create/update/delete use axios directly). If the queue is extended to
        // handle other operations in the future, this logic would need request-ID-based tracking
        // to determine when a specific readAllRequest chain (including its spawned relationship
        // requests) is complete, rather than relying on queue.length === 0.
        if (this.queue.length === 0 && this.isCountReceived) {
          // Snapshot counters that get reset below — the timing summary is
          // deferred until pending batches drain, by which time these are 0.
          if (this._timing) {
            this._timing.skipped = this.syncSkippedIds.length
          }

          console.log(
            `ADT: Sync complete - processed ${this.twinsLoaded} twins (${this.actualIndividualsDispatched} total individuals including components, ${this.syncSkippedIds.length} skipped by etagMap)`
          )

          // Notify the cache about twins that were seen during sync but skipped
          // by the etagMap filter (unchanged, not re-parsed). The cache needs
          // these IDs to avoid mistaking skipped twins for deleted ones.
          if (this.syncSkippedIds.length > 0) {
            dispatchEvent(
              new CustomEvent(SyncSkippedIdsEvent, {
                detail: this.syncSkippedIds,
              })
            )
          }
          this.syncSkippedIds = []

          dispatchEvent(new CustomEvent(CacheSyncCompleteEvent))

          // Prune etagMap entries for twins no longer in ADT.
          if (this.syncSeenIds) {
            for (const [twinId] of this.etagMap) {
              if (!this.syncSeenIds.has(twinId)) {
                this.etagMap.delete(twinId)
              }
            }
            this.syncSeenIds = null
          }

          // Persistence is the cache's job: it saves this map with the
          // individuals, in the same IndexedDB transaction.

          // Timing summary is deferred until the final batch has been
          // dispatched — _dispatchMembersBatched yields between batches, so
          // the last page's dispatch cost lands after this point. The call
          // itself is at the end of this handler, after the dispatch is
          // started; calling it here reported before the final page had been
          // dispatched at all, showing 0 individuals whenever the last page
          // contained changed twins.
          this._syncCompleted = true

          // Reset for next readAll cycle
          this.actualIndividualsDispatched = 0
          this.twinsLoaded = 0
          this.isCountReceived = false
        }

        // Start next pending request cycle if queue is not empty.
        // Placed before batch dispatch so network I/O overlaps with event processing.
        const [first] = this.queue
        if (first) this._postToWorker(first)

        // Batch dispatch individuals, yielding between batches to keep UI responsive.
        if (individualsToBatch) {
          this._dispatchMembersBatched(individualsToBatch)
        }

        // Now that the final page's dispatch has been started, report. If
        // batches are still draining this defers, and the last batch to
        // finish calls it again.
        this._maybeLogTimingSummary()
      }
    }

    this.worker.onerror = (error) => {
      console.log('wob-client.adt-client.worker-on-error', error)
    }
  }

  init() {
    console.log('wob-client.adt-client.init')
  }

  // Used to change the origin after initialization.
  setOrigin(origin) {
    if (origin !== this.origin) {
      this.clearEtagMap()
    }
    this.origin = origin
  }

  queueRequest(request) {
    console.log('wob-client.adt-client.add-request', request)
    // TODO may push to queue first to ensure that the worker doenst return immediately and the event handler doesnt find the request in teh queue.
    if (this.queue.length == 0) this._postToWorker(request)
    this.queue.push(request)
  }

  // Single funnel to the worker so page counts stay accurate in one place.
  _postToWorker(request) {
    if (this._timing) this._timing.posts++
    this.worker.postMessage(request)
  }

  async readAllRequest({ isCacheHydrated = false } = {}) {
    console.log('wob-client.adt-client.read-all-request')

    // If the caller indicates no data was hydrated into the cache, clear the
    // persisted etagMap — there's nothing in memory for the filter to protect.
    if (!isCacheHydrated) {
      this.clearEtagMap()
    }

    // Refresh token before starting sync cycle
    await this.refreshToken()

    // Reset per-phase timing for the new cycle.
    this._timing = {
      t0: performance.now(),
      posts: 0,
      filter: 0,
      parse: 0,
      dispatch: 0,
      twinPages: 0,
      relPages: 0,
      relContinuations: 0,
      individuals: 0,
      skipped: 0,
    }
    this._pendingBatches = 0
    this._syncCompleted = false

    // Reset count tracking for new readAll cycle
    this.isCountReceived = false
    this.collectionSize = null
    this.actualIndividualsDispatched = 0
    this.twinsLoaded = 0
    this.syncSeenIds = null
    this.syncSkippedIds = []

    // First, queue a COUNT query to get twin count (for logging only)
    // Note: COUNT returns twins only, not components
    // Actual size will be dispatched when pagination completes
    this.queueRequest(
      new Request(
        this.queryRequestConfigFactory('SELECT COUNT() FROM DIGITALTWINS')
      )
    )

    // Then queue the main SELECT query
    // This will start after COUNT completes
    return this.queueRequest(
      new Request(this.queryRequestConfigFactory('SELECT * FROM DIGITALTWINS'))
    )
  }

  // Utility to form an ADT query request.
  // TODO refactor forming of headers...
  queryRequestConfigFactory(query, pagination = true) {
    const headers = pagination
      ? Object.assign({}, this.authHeader, this.pageSizeHeader, {
          'content-type': 'application/json',
        })
      : Object.assign({}, this.authHeader, {
          'content-type': 'application/json',
        })
    return new RequestConfig(
      AdtClient.proxyUrl(`${this.origin}/query${this.queryFlags}`),
      headers,
      JSON.stringify({ query: query }),
      'POST'
    )
  }
  // Utility to form an ADT continuation request. `pagination` should match
  // the query being continued (see queryRequestConfigFactory).
  continuationRequestConfigFactory(contToken, pagination = true) {
    const headers = pagination
      ? Object.assign({}, this.authHeader, this.pageSizeHeader, {
          'content-type': 'application/json',
        })
      : Object.assign({}, this.authHeader, {
          'content-type': 'application/json',
        })
    return new RequestConfig(
      AdtClient.proxyUrl(`${this.origin}/query${this.queryFlags}`),
      headers,
      JSON.stringify({ continuationToken: contToken }),
      'POST'
    )
  }

  setToken(token) {
    // Never log the token itself — it is a bearer credential with the
    // full ADT data-plane scope and console logs get pasted into issues.
    console.log('wob-client.adt-client.set-token', token ? '<redacted>' : null)

    if (this.token == token) return

    // Abort ongoing read cycles at token change.
    // Log ongoing request to abort ongoing paginated request cycle.
    const [currentReq] = this.queue
    this.abortReqId = currentReq?.id
    this.queue = []

    this.token = token
    this.authHeader = token ? { Authorization: `Bearer ${this.token}` } : {}
  }

  // Silently refreshes the access token using the configured tokenProvider.
  // Called before API requests to ensure the token is fresh.
  // MSAL's acquireTokenSilent returns cached tokens if valid,
  // or automatically uses the refresh token when the access token has expired.
  async refreshToken() {
    if (!this.tokenProvider) return
    try {
      const token = await this.tokenProvider()
      if (token) {
        this.token = token
        this.authHeader = { Authorization: `Bearer ${token}` }
      }
    } catch (error) {
      console.error('wob-client.adt-client.refresh-token.error', error)
    }
  }

  // Request to the digitaltwins endpoint.
  // TODO not used, consider abstracting when more API requests have been implemented
  // TODO introduce etag/if-match when error handling is in place

  async twinsRequestConfigFactory() {}

  // Parameter type is an Iri.
  async createRequest(type, existingId) {
    try {
      await this.refreshToken()

      // Form type into a DTDL model.
      const model = toDTDL(type.value)
      console.log('wob-client.adt-client.adt-client.create-request', type)
      console.log(
        'wob-client.adt-client.adt-client.create-request.model',
        model
      )

      const id = existingId || this.idFactory()
      console.log('wob-client.adt-client.adt-client.create-request.id', id)

      // Form and execure request on ADT API - manipulation request factory?
      //     PUT https://digitaltwins-hostname/digitaltwins/myNewTwinId?api-version=2023-10-31
      // {
      //   "$metadata": {
      //     "$model": "dtmi:com:example:interfaces:interfaceName;1"
      //   }
      // }

      // TODO create factory, use the parser definitions for keys...
      const body = { $metadata: { $model: model } }

      // TODO add empty components to body...
      // Figure if type or any superclass comprise components...
      // Best would be to read the models...
      // For testing a list of all architecture mdoels are used...

      // Note this takes place in the RDF/REC domain. Not DTDL.
      const isArchitecture = ARCHITECTURE_MODELS.includes(type.value)
      console.log('ADT CLIENT IS ARCH', isArchitecture)

      // The body is expanded with empty components.
      if (isArchitecture) {
        body.area = { $metadata: {} }
        body.capacity = { $metadata: {} }
      }

      const config = new RequestConfig(
        AdtClient.proxyUrl(
          `${this.origin}/digitaltwins/${id}${this.queryFlags}`
        ),
        Object.assign({}, this.authHeader, {
          'content-type': 'application/json',
        }),
        JSON.stringify(body),
        'PUT'
      )
      console.log(
        'wob-client.adt-client.adt-client.create-request.config',
        config
      )

      const { data } = await axios(config)

      // Update etagMap: new twin has no relationships.
      this.etagMap.set(data['$dtId'], {
        twinEtag: data['$etag'],
        relHash: cyrb53(''),
      })

      console.log('wob-client.adt-client.adt-client.create-request.data', data)

      // TODO the adt parser and later the owl parser must be refacired to comply with JSON-LD (the adt parser seems to be ok, the OWL parser expect object not array)
      // TODO somehow perhasop the owl parser should be able to deal with several individuals in the received and expanded graph
      const jsonlds = await adtParser(data)

      console.log('JSONLDS', jsonlds)

      console.log(
        'wob-client.adt-client.adt-client.create-request.json-ld',
        jsonlds
      )
      // TODO note deconstruction the JSONLD which seems rigth formed (an array) but the owl parser seem to require an object...
      jsonlds.forEach(([jsonld]) => {
        dispatchEvent(
          new CustomEvent(IndividualRefreshEvent, { detail: jsonld })
        )
      })

      // dispatchEvent(new CustomEvent(IndividualRefreshEvent, { detail: jsonld }))

      return Promise.resolve(id)
    } catch (error) {
      console.log(
        'wob-client.adt-client.adt-client.create-request.error',
        error
      )
      // TODO in case of error the generalization of error code/message/name must happen here
      return Promise.reject()
    }
  }

  // Parameter id is the ADT twin id string.
  async deleteTwinRequest(id) {
    try {
      await this.refreshToken()

      // DELETE https://digitaltwins-hostname/digitaltwins/{id}?api-version=2023-10-31

      const config = new RequestConfig(
        AdtClient.proxyUrl(
          `${this.origin}/digitaltwins/${id}${this.queryFlags}`
        ),
        Object.assign({}, this.authHeader, {
          'content-type': 'application/json',
        }),
        null,
        'DELETE'
      )
      console.log(
        'wob-client.adt-client.adt-client.delete-twin-request.config',
        config
      )
      await axios(config)
    } catch (error) {
      console.log(
        'wob-client.adt-client.adt-client.delete-twin-request.error',
        error
      )
      throw error
    }
  }

  // Parameters are ADT id strings.
  async deleteRelationshipRequest(twinId, relationshipId) {
    try {
      await this.refreshToken()

      // DELETE https://digitaltwins-hostname/digitaltwins/myTwinId/relationships/myRelationshipId?api-version=2023-10-31
      const config = new RequestConfig(
        AdtClient.proxyUrl(
          `${this.origin}/digitaltwins/${twinId}/relationships/${relationshipId}${this.queryFlags}`
        ),
        Object.assign({}, this.authHeader, {
          'content-type': 'application/json',
        }),
        null,
        'DELETE'
      )
      console.log(
        'wob-client.adt-client.adt-client.delete-twin-request.config',
        config
      )
      await axios(config)
    } catch (error) {
      console.log(
        'wob-client.adt-client.adt-client.delete-twin-request.error',
        error
      )
      throw error
    }
  }

  // Parameter id is the ADT twin id string.
  // Returns array of outgoing ADT relationships.
  // Handles pagination to retrieve ALL relationships (not limited to 1000).
  async listRelationshipsRequest(id) {
    // GET https://digitaltwins-hostname/digitaltwins/mySourceTwin/relationships?api-version=2023-10-31
    try {
      let allRelationships = []
      let nextLink = null
      let pageCount = 0

      // Pagination loop: continue while nextLink exists
      do {
        // Refresh token on each page to handle long-running paginated requests
        await this.refreshToken()

        const url = nextLink
          ? nextLink
          : `${this.origin}/digitaltwins/${id}/relationships${this.queryFlags}`

        const config = new RequestConfig(
          AdtClient.proxyUrl(url),
          Object.assign({}, this.authHeader, this.pageSizeHeader, {
            'content-type': 'application/json',
          }),
          null,
          'GET'
        )

        console.log(
          `wob-client.adt-client.list-relationships-request [page ${
            pageCount + 1
          }]`,
          config.url
        )

        const { data } = await axios(config)
        const { value: relationships, nextLink: next } = data

        allRelationships = allRelationships.concat(relationships)
        nextLink = next
        pageCount++

        console.log(
          `wob-client.adt-client.list-relationships-request [page ${pageCount}]: loaded ${
            relationships.length
          } relationships, total: ${
            allRelationships.length
          }, hasMore: ${!!nextLink}`
        )
      } while (nextLink)

      console.log(
        `wob-client.adt-client.list-relationships-request: completed with ${allRelationships.length} total relationships across ${pageCount} pages`
      )

      return allRelationships
    } catch (error) {
      console.log(
        'wob-client.adt-client.adt-client.list-relationships-request.error',
        error
      )
      throw error
    }
  }

  // Parameter id is the ADT twin id string.
  // Returns array of incoming ADT relationships.
  // Handles pagination to retrieve ALL relationships (not limited to 1000).
  async listIncomingRelationshipsRequest(id) {
    // GET https://digitaltwins-hostname/digitaltwins/mySourceTwin/incomingrelationships?api-version=2023-10-31
    try {
      let allRelationships = []
      let nextLink = null
      let pageCount = 0

      // Pagination loop: continue while nextLink exists
      do {
        // Refresh token on each page to handle long-running paginated requests
        await this.refreshToken()

        const url = nextLink
          ? nextLink
          : `${this.origin}/digitaltwins/${id}/incomingrelationships${this.queryFlags}`

        const config = new RequestConfig(
          AdtClient.proxyUrl(url),
          Object.assign({}, this.authHeader, this.pageSizeHeader, {
            'content-type': 'application/json',
          }),
          null,
          'GET'
        )

        console.log(
          `wob-client.adt-client.list-incoming-relationships-request [page ${
            pageCount + 1
          }]`,
          config.url
        )

        const { data } = await axios(config)
        const { value: relationships, nextLink: next } = data

        allRelationships = allRelationships.concat(relationships)
        nextLink = next
        pageCount++

        console.log(
          `wob-client.adt-client.list-incoming-relationships-request [page ${pageCount}]: loaded ${
            relationships.length
          } relationships, total: ${
            allRelationships.length
          }, hasMore: ${!!nextLink}`
        )
      } while (nextLink)

      console.log(
        `wob-client.adt-client.list-incoming-relationships-request: completed with ${allRelationships.length} total relationships across ${pageCount} pages`
      )

      return allRelationships
    } catch (error) {
      console.log(
        'wob-client.adt-client.adt-client.list-incoming-relationships-request.error',
        error
      )
      throw error
    }
  }

  // Deletes an ADT twin and all incoimg and outgoing relationships. No guard rails.
  // Parameter id is an Iri.
  async deleteRequest(id) {
    console.log('wob-client.adt-client.adt-client.delete-request', id)

    try {
      await this.refreshToken()

      if (id.value.startsWith('anonymous:')) {
        // The individual is a component which doesnt exist in ADT and hence cannot be deleted.
        // It will be deleted as part of the indvidual of which it is part.
        // TODO verify...
      } else {
        // TODO add a cache at reading so that these two requests may be avoided...
        const outgoing = await this.listRelationshipsRequest(id.value)
        const outDeleteFns = outgoing.map(
          (r) => () =>
            withRetry(() =>
              this.deleteRelationshipRequest(id.value, r['$relationshipId'])
            )
        )
        await sequential(outDeleteFns)

        const incoming = await this.listIncomingRelationshipsRequest(id.value)
        // response format
        //  {
        //     "$relationshipId": "34acf454-5a13-45e6-9a7d-ee12e02cc3fc",
        //     "$sourceId": "5bf763d0-cea2-42df-a9c6-d78139bc5ab9",
        //     "$relationshipName": "includes",
        //     "$relationshipLink": "https://sfab-pilot.api.neu.digitaltwins.azure.net/digitaltwins/5bf763d0-cea2-42df-a9c6-d78139bc5ab9/relationships/34acf454-5a13-45e6-9a7d-ee12e02cc3fc?api-version=2023-10-31"
        // }

        console.log(
          'wob-client.adt-client.adt-client.delete-request.relationships.incoming',
          incoming
        )
        const inDeleteFns = incoming.map(
          (r) => () =>
            withRetry(() =>
              this.deleteRelationshipRequest(
                r['$sourceId'],
                r['$relationshipId']
              )
            )
        )
        await sequential(inDeleteFns)

        await this.deleteTwinRequest(id.value)

        // Remove deleted twin from etagMap.
        this.etagMap.delete(id.value)
      }

      dispatchEvent(
        new CustomEvent(IndividualDeleteEvent, { detail: id.value })
      )
    } catch (error) {
      console.log(
        'wob-client.adt-client.adt-client.delete-request.error',
        error
      )
      throw error
    }
  }

  // Add a relationship to ADT.
  // All parameters are strings.
  async addRelationshipRequest(twinId, targetId, relationshipName) {
    // PUT https://digitaltwins-hostname/digitaltwins/{id}/relationships/{relationshipId}?api-version=2023-10-31
    try {
      await this.refreshToken()

      // TODO the relationship id is using the same id factory as the twins... consider if this has reason to be two.
      const relationshipId = this.idFactory()

      const config = new RequestConfig(
        AdtClient.proxyUrl(
          `${this.origin}/digitaltwins/${twinId}/relationships/${relationshipId}${this.queryFlags}`
        ),
        Object.assign({}, this.authHeader, {
          'content-type': 'application/json',
        }),
        {
          $targetId: targetId,
          $relationshipName: relationshipName,
        },
        'PUT'
      )
      console.log(
        'wob-client.adt-client.adt-client.add-relationship-request.config',
        config
      )
      const { data: relationship } = await axios(config)
      console.log(
        'wob-client.adt-client.adt-client.add-relationship-request.relationship',
        relationship
      )
      return relationship
    } catch (error) {
      console.log(
        'wob-client.adt-client.adt-client.add-relationship-request.error',
        error
      )
      throw error
    }
  }

  // DELETE https://digitaltwins-hostname/digitaltwins/{id}/relationships/{relationshipId}?api-version=2023-10-31

  // Read an ADT twin.
  // Parameter id is an iri string.
  // Returns twin in ADT format.
  async readTwinRequest(id) {
    // GET https://digitaltwins-hostname/digitaltwins/{id}?api-version=2023-10-31

    try {
      await this.refreshToken()

      const config = new RequestConfig(
        AdtClient.proxyUrl(
          `${this.origin}/digitaltwins/${id}${this.queryFlags}`
        ),
        Object.assign({}, this.authHeader, {
          'content-type': 'application/json',
        }),
        null,
        'GET'
      )
      console.log(
        'wob-client.adt-client.adt-client.read-twin-request.config',
        config
      )
      const { data: twin } = await axios(config)
      console.log(
        'wob-client.adt-client.adt-client.read-twin-request.twin',
        twin
      )
      return twin
    } catch (error) {
      console.log(
        'wob-client.adt-client.adt-client.read-twin-request.error',
        error
      )
      throw error
    }
  }

  // Read a single individual from ADT with fresh properties and relationships,
  // then update the in-memory cache via the existing event system.
  // Reuses readTwinRequest, listRelationshipsRequest, and the relationship merge
  // pattern from the bulk sync flow (worker onmessage handler).
  // Parameter id is an iri string.
  async readIndividualRequest(id) {
    // Fetch twin and relationships in parallel.
    const [twin, relationships] = await Promise.all([
      this.readTwinRequest(id),
      this.listRelationshipsRequest(id),
    ])

    // Merge relationships into the twin object (same pattern as bulk sync lines 283-295).
    relationships.forEach((r) => {
      const relationshipName = r['$relationshipName']
      const targetId = r['$targetId']
      if (twin[relationshipName]) twin[relationshipName].push(targetId)
      else twin[relationshipName] = [targetId]
    })

    // ETag+relHash check: skip parsing if nothing changed (same pattern as bulk sync lines 298-327).
    const metaKeys = ['$dtId', '$metadata', '$etag']
    const relPairs = []
    for (const [key, value] of Object.entries(twin)) {
      if (metaKeys.includes(key)) continue
      if (Array.isArray(value)) {
        value.forEach((targetId) => relPairs.push(`${key}:${targetId}`))
      }
    }
    const relHash = cyrb53(relPairs.sort().join('|'))

    const stored = this.etagMap.get(id)
    if (
      stored &&
      stored.twinEtag === twin['$etag'] &&
      stored.relHash === relHash
    ) {
      console.log('wob-client.adt-client.read-individual-request.unchanged', id)
      return
    }

    this.etagMap.set(id, { twinEtag: twin['$etag'], relHash })

    // Parse and dispatch refresh events (same pattern as createRequest lines 669-681).
    const jsonlds = await adtParser(twin)
    jsonlds.forEach(([jsonld]) => {
      dispatchEvent(new CustomEvent(IndividualRefreshEvent, { detail: jsonld }))
    })
  }

  async updateTwinRequest(id, body) {
    // PATCH https://digitaltwins-hostname/digitaltwins/{id}?api-version=2023-10-31
    // Note content type.
    try {
      await this.refreshToken()

      const config = new RequestConfig(
        AdtClient.proxyUrl(
          `${this.origin}/digitaltwins/${id}${this.queryFlags}`
        ),
        Object.assign({}, this.authHeader, {
          'content-type': 'application/json-patch+json',
        }),
        body,
        'PATCH'
      )
      console.log(
        'wob-client.adt-client.adt-client.update-twin-request.config',
        config
      )
      const { data: twin } = await axios(config)
      console.log(
        'wob-client.adt-client.adt-client.update-twin-request.twin',
        twin
      )
      return twin
    } catch (error) {
      console.log(
        'wob-client.adt-client.adt-client.update-twin-request.error',
        error
      )
      throw error
    }
  }

  // Parameter jsonld is expanded JSON-LD.
  // TODO see over the naming of variable, inconsistent use of twin, updated, current, key, value, props, outgoing etc...
  async updateRequest(jsonld) {
    console.log('wob-client.adt-client.adt-client.update-request', jsonld)

    // Refresh token at entry point - inner methods also refresh for long-running operations
    await this.refreshToken()

    const excludedProperties = [
      '@id',
      '@type',
      'http://purl.org/dc/terms/modified',
      'http://purl.org/dc/terms/created',
    ]

    // Utility function to transform an iri to a dtdl identifier through the context.
    function iriToDtdl(iri) {
      const [id] = Object.entries(context).find(
        ([, value]) => value['@id'] == iri
      )
      return id
    }

    try {
      // Exclude meta properties and divide the received to be updated JSON-LD individual in props and relationships (outgoing).
      // Only the first object of the expanded JSON-LD is handled, ie no support for graphs, only one node.
      // The prop x value arrays are transformed to arrays of prop, value arrays -> [prop, value][]
      const [updated] = jsonld
      const { ['@id']: id } = updated
      // Transform JSON-LD individual into key-value pairs for processing
      // Each property can have multiple values, so we flatten: prop -> [value1, value2] becomes [[prop, value1], [prop, value2]]
      const updatedKvPairs = Object.entries(updated)
        .filter(([key]) => !excludedProperties.includes(key))
        .map(([key, values]) => values.map((v) => [key, v]))
        .reduce((acc, kvPairs) => acc.concat(kvPairs), [])

      // ALGORITHM OVERVIEW: Three-Flow Property Update System
      // =====================================================
      // The update algorithm splits properties into three processing flows to handle different data types:
      //
      // 1. JSON LITERALS: Complex objects (e.g., identifiers: {"buildingId": "B123"})
      //    - Challenge: Object comparison always fails due to reference inequality
      //    - Solution: Full object replacement (add/replace/remove entire objects)
      //    - Detection: Properties with @type: "@json"
      //
      // 2. REGULAR PROPERTIES: Simple literals (strings, numbers, booleans)
      //    - Challenge: Standard value comparison works fine
      //    - Solution: Existing comparison logic (value equality)
      //    - Detection: Properties with @value but no @type: "@json"
      //
      // 3. RELATIONSHIPS: References to other individuals
      //    - Challenge: Object references via @id
      //    - Solution: Existing relationship handling (unchanged)
      //    - Detection: Properties with @id

      // Split processing into three flows based on JSON-LD value type:

      // 1. JSON LITERALS FLOW: Handle with full object replacement (add/replace/remove entire objects)
      // These have @type: "@json" and contain complex objects that need special comparison logic
      const updatedJsonProps = updatedKvPairs
        .filter(([, value]) => value['@type'] === '@json')
        .map(([key, value]) => [key, value['@value']])

      // 2. REGULAR PROPERTIES FLOW: Handle with existing value comparison logic
      // These are simple literals (strings, numbers) that can be compared directly
      const updatedProps = updatedKvPairs
        .filter(([, value]) => '@value' in value && value['@type'] !== '@json')
        .map(([key, value]) => [key, value['@value']])

      // 3. RELATIONSHIPS FLOW: Handle with existing relationship logic (unchanged)
      // These point to other individuals via @id references
      const updatedRels = updatedKvPairs
        .filter(([, value]) => '@id' in value)
        .map(([key, value]) => [key, value['@id']])

      console.log('KV PAIRS', updatedKvPairs)
      console.log('JSON PROPS', updatedJsonProps)
      console.log('REGULAR PROPS', updatedProps)
      console.log('RELS', updatedRels)

      const twin = await this.readTwinRequest(id)
      console.log('wob-client.adt-client.adt-client.update-request.twin', twin)

      // The ADT parser returns an array of arrays: [individual, components...] but we need the main individual
      const adtParserResult = await adtParser(twin)

      // COMPONENT WORKAROUND: Find the main individual by ID rather than using array destructuring
      // =====================================================================================
      // The ADT parser returns multiple arrays: [area_component, capacity_component, main_individual]
      // Previous code used [[twinJsonld]] which always took the first array (area component)
      //
      // WORKAROUND: Find the individual with the real twin ID (not anonymous component IDs)
      // This works as long as components are not manipulated directly in the UI (which they aren't)
      //
      // FUTURE: If components become editable in the UI, this logic needs to be redesigned
      // to properly handle component updates vs main individual updates
      const twinJsonld = adtParserResult
        .map((arr) => arr[0]) // Get first element from each array
        .find((individual) => individual['@id'] === id) // Find the one with our twin ID
      console.log(
        'wob-client.adt-client.adt-client.update-request.twin-expanded',
        twinJsonld
      )

      // Process current twin data with same three-flow approach as updated data
      // Transform current twin's JSON-LD into key-value pairs for comparison
      const twinKvPairs = Object.entries(twinJsonld)
        .filter(([key]) => !excludedProperties.includes(key))
        .map(([key, values]) => values.map((v) => [key, v]))
        .reduce((acc, kvPairs) => acc.concat(kvPairs), [])

      // Extract JSON literals from current twin for comparison with updated JSON literals
      const twinJsonProps = twinKvPairs
        .filter(([, value]) => '@value' in value && value['@type'] === '@json')
        .map(([key, value]) => [key, value['@value']])

      // Extract regular properties from current twin for existing comparison logic
      const twinProps = twinKvPairs
        .filter(([, value]) => '@value' in value && value['@type'] !== '@json')
        .map(([key, value]) => [key, value['@value']])

      console.log('TWIN JSON PROPS', twinJsonProps)
      console.log('TWIN REGULAR PROPS', twinProps)

      // REGULAR PROPERTIES: Use existing comparison logic for simple literals
      // These can be compared directly as they're strings, numbers, etc.
      const addProps = updatedProps.filter(
        ([key, value]) => !twinProps.find(([k, v]) => k == key && v == value)
      )

      const removeProps = twinProps.filter(
        ([key, value]) => !updatedProps.find(([k, v]) => k == key && v == value)
      )

      console.log('ADD PROPS', addProps)
      console.log('REMOVE PROPS', removeProps)

      // JSON LITERALS: Use full object replacement logic (no value comparison)
      // Objects need special handling because reference equality always fails
      const addOrReplaceJsonProps = []
      const removeJsonProps = []

      // Process each JSON literal in updated data
      updatedJsonProps.forEach(([key, value]) => {
        const existsInTwin = twinJsonProps.some(([k]) => k === key)

        if (existsInTwin) {
          // Property exists: always replace with full object (avoids complex object comparison)
          addOrReplaceJsonProps.push([key, value, 'replace'])
        } else {
          // Property doesn't exist: add with full object
          addOrReplaceJsonProps.push([key, value, 'add'])
        }
      })

      // Find JSON literals that exist in twin but not in updated (need removal)
      twinJsonProps.forEach(([key]) => {
        const stillWanted = updatedJsonProps.some(([k]) => k === key)
        if (!stillWanted) {
          removeJsonProps.push(key)
        }
      })

      console.log('ADD/REPLACE JSON PROPS', addOrReplaceJsonProps)
      console.log('REMOVE JSON PROPS', removeJsonProps)

      // The relationships (outgoing) to create or delete are identified.
      // Read all outgoing relationship on twin.
      const relationships = await this.listRelationshipsRequest(updated['@id'])
      console.log(
        'wob-client.adt-client.adt-client.update-request.relationships',
        relationships
      )

      // Identify relationships to add, ie the same relationship is not present on the twin.
      // TODO update algo so it isnt necessary to find rel name twice...
      const addRels = updatedRels
        .filter(([rel, target]) => {
          // The relationship name is identified through the context, ie ADT relationship name from REC iri.
          const [relationshipName] = Object.entries(context).find(
            ([, value]) => value['@id'] == rel
          )

          console.log('CREATE RELS ALGO', rel, target, relationshipName)

          const existing = relationships.find(
            (r) =>
              r['$relationshipName'] == relationshipName &&
              r['$targetId'] == target
          )
          return !existing
        })
        .map(([rel, target]) => {
          const [relationshipName] = Object.entries(context).find(
            ([, value]) => value['@id'] == rel
          )
          return [relationshipName, target]
        })

      console.log('ADD RELS', addRels)

      // Identify relationships to remove. Note this list follows the ADT format.

      const deleteRels = relationships.filter((relationship) => {
        // Transform from ADT relationship name to REC iri.
        const rel = context[relationship['$relationshipName']]['@id']
        const target = relationship['$targetId']
        console.log('DELETE RELS ALGO', rel, target)
        const existing = updatedRels.find(([r, t]) => r == rel && t == target)
        return !existing
      })

      console.log('DELETE RELS', deleteRels)

      // Create body for the patch twin request.
      // Combines regular properties and JSON literals into a single JSON Patch array

      // TODO conversion of RDF iris to DTDL ids happens here as the previous comparisons for changes are done with iris.
      // Likely better to convert earlier and compare with DTDL identifers (ie short names)
      const body = []

      // REGULAR PROPERTIES: Use existing logic
      // Remove undertaken before add to ensure that the same property is not present twice.
      removeProps.forEach(([prop]) => {
        body.push({ op: 'remove', path: '/' + iriToDtdl(prop) })
      })
      addProps.forEach(([prop, value]) => {
        body.push({ op: 'add', path: '/' + iriToDtdl(prop), value: value })
      })

      // JSON LITERALS: Use full object replacement logic
      // Remove JSON literals first (if any need to be removed)
      removeJsonProps.forEach((prop) => {
        body.push({ op: 'remove', path: '/' + iriToDtdl(prop) })
      })

      // Add or replace JSON literals with full objects
      addOrReplaceJsonProps.forEach(([prop, value, operation]) => {
        body.push({
          op: operation, // 'add' for new properties, 'replace' for existing ones
          path: '/' + iriToDtdl(prop),
          value: value, // Full JSON object (e.g., {"buildingId": "B123", "facilityCode": "FAC456"})
        })
      })

      console.log('PATCH BODY', body)

      // A relationship-only change produces no property operations, and ADT
      // models relationships as separate resources rather than twin patches.
      // Sending an empty patch is a wasted round-trip that cannot succeed at
      // anything — and because it is awaited before the relationship writes
      // below, any failure of it (proxy hiccup, transient 5xx) aborts the
      // relationship change that was the actual point of the update.
      const patchedTwin = body.length
        ? await this.updateTwinRequest(id, body)
        : null

      // Add relationships (sequentially with retry to avoid 429 rate limiting).
      const addRelFns = addRels.map(
        ([rel, target]) =>
          () =>
            withRetry(() => this.addRelationshipRequest(id, target, rel))
      )
      const addResponses = await sequential(addRelFns)
      console.log('ADDED RELS', addResponses)

      // Delete relationships (sequentially with retry to avoid 429 rate limiting).
      const deleteRelFns = deleteRels.map(
        (r) => () =>
          withRetry(() =>
            this.deleteRelationshipRequest(id, r['$relationshipId'])
          )
      )
      const deleteResponses = await sequential(deleteRelFns)
      console.log('DELETE RELS', deleteResponses)

      // Update etagMap with patched twin etag and final relationship state.
      // Final rels = current outgoing - deleted + added.
      const finalRels = relationships
        .filter(
          (r) =>
            !deleteRels.some(
              (d) => d['$relationshipId'] === r['$relationshipId']
            )
        )
        .concat(
          addRels.map(([rel, target]) => ({
            $relationshipName: rel,
            $targetId: target,
          }))
        )
      const relHash = cyrb53(
        finalRels
          .map((r) => `${r['$relationshipName']}:${r['$targetId']}`)
          .sort()
          .join('|')
      )
      // With no property patch there is no fresh twin etag — but ADT models
      // relationships as separate resources, so a relationship-only change
      // does not alter the twin document and the etag read at the top of this
      // method is still current.
      this.etagMap.set(id, {
        twinEtag: (patchedTwin || twin)['$etag'],
        relHash,
      })

      // Dispatch refresh event with the original JSON-LD representation.
      // Note: toJSONLD() serializes components (area, capacity) as inline blank
      // nodes that owl.parse() cannot round-trip. The rec-cache refresh() method
      // compensates by preserving existing down-aggregator ObjectProperties when
      // they are missing from the incoming individual.
      dispatchEvent(
        new CustomEvent(IndividualRefreshEvent, { detail: updated })
      )
    } catch (error) {
      console.log(
        'wob-client.adt-client.adt-client.update-request.error',
        error
      )
      throw error
    }
  }

  // Change the type of an existing twin by delete + recreate with same ID.
  // Parameters:
  //   id - Iri object with .value being the ADT twin ID string
  //   newType - Iri object with .value being the REC/Brick ontology IRI
  // Returns the twin ID string on success.
  // Throws an error object with details on failure: { step, journal, error }
  async changeTypeRequest(id, newType) {
    const twinId = id.value

    console.log(
      'wob-client.adt-client.change-type-request',
      twinId,
      newType.value
    )

    // ADT metadata keys excluded from data property snapshot
    const adtMetaKeys = ['$dtId', '$metadata', '$etag']

    // Step 1: Snapshot current state
    const twin = await this.readTwinRequest(twinId)
    const outgoing = await this.listRelationshipsRequest(twinId)
    const incoming = await this.listIncomingRelationshipsRequest(twinId)

    // Extract data properties (everything except ADT metadata and components)
    const componentKeys = ['area', 'capacity', 'lastknownvalue']
    const dataProperties = {}
    for (const [key, value] of Object.entries(twin)) {
      if (
        !adtMetaKeys.includes(key) &&
        !componentKeys.includes(key.toLowerCase())
      ) {
        dataProperties[key] = value
      }
    }

    const journal = {
      version: 1,
      state: 'started',
      createdAt: new Date().toISOString(),
      twinId,
      oldModel: twin.$metadata.$model,
      newType: newType.value,
      snapshot: {
        twin,
        dataProperties,
        outgoing,
        incoming,
      },
    }

    ChangeTypeJournal.write(journal)

    try {
      // Step 2: Delete all relationships (sequentially to avoid 429 rate limiting)
      const outDeleteFns = outgoing.map(
        (r) => () =>
          withRetry(() =>
            this.deleteRelationshipRequest(twinId, r['$relationshipId'])
          )
      )
      await sequential(outDeleteFns)

      const inDeleteFns = incoming.map(
        (r) => () =>
          withRetry(() =>
            this.deleteRelationshipRequest(r['$sourceId'], r['$relationshipId'])
          )
      )
      await sequential(inDeleteFns)

      journal.state = 'rels-deleted'
      ChangeTypeJournal.write(journal)

      // Step 3: Delete the twin
      await this.deleteTwinRequest(twinId)

      // Dispatch delete event so cache removes the old twin
      dispatchEvent(new CustomEvent(IndividualDeleteEvent, { detail: twinId }))

      journal.state = 'deleted'
      ChangeTypeJournal.write(journal)

      // Step 4: Create new twin with same ID and new type
      await this.createRequest(newType, twinId)

      journal.state = 'created'
      ChangeTypeJournal.write(journal)

      // Step 5: Patch data properties onto new twin
      const patchBody = Object.entries(dataProperties).map(([key, value]) => ({
        op: 'add',
        path: '/' + key,
        value,
      }))

      if (patchBody.length > 0) {
        await this.updateTwinRequest(twinId, patchBody)
      }

      journal.state = 'patched'
      ChangeTypeJournal.write(journal)

      // Step 6: Restore all relationships (sequentially to avoid 429 rate limiting)
      const restoreOutFns = outgoing.map(
        (r) => () =>
          withRetry(() =>
            this.addRelationshipRequest(
              twinId,
              r['$targetId'],
              r['$relationshipName']
            )
          )
      )
      await sequential(restoreOutFns)

      const restoreInFns = incoming.map(
        (r) => () =>
          withRetry(() =>
            this.addRelationshipRequest(
              r['$sourceId'],
              twinId,
              r['$relationshipName']
            )
          )
      )
      await sequential(restoreInFns)

      journal.state = 'completed'
      ChangeTypeJournal.write(journal)

      // Step 7: Refresh the cache with the final state
      const finalTwin = await this.readTwinRequest(twinId)
      const jsonlds = await adtParser(finalTwin)
      jsonlds.forEach(([jsonld]) => {
        dispatchEvent(
          new CustomEvent(IndividualRefreshEvent, { detail: jsonld })
        )
      })

      // Update etagMap with final twin etag and restored outgoing relationships.
      const relHash = cyrb53(
        outgoing
          .map((r) => `${r['$relationshipName']}:${r['$targetId']}`)
          .sort()
          .join('|')
      )
      this.etagMap.set(twinId, {
        twinEtag: finalTwin['$etag'],
        relHash,
      })

      // Clean up journal — transaction fully complete
      ChangeTypeJournal.clear()

      return twinId
    } catch (error) {
      console.error(
        'wob-client.adt-client.change-type-request.error',
        'Failed at state:',
        journal.state,
        error
      )

      // Attempt rollback if twin was deleted but not yet recreated
      if (journal.state === 'deleted') {
        console.log(
          'wob-client.adt-client.change-type-request: Attempting rollback — recreating with old type'
        )
        try {
          await this._rollbackChangeType(journal)
          journal.state = 'rolled-back'
          ChangeTypeJournal.write(journal)
          ChangeTypeJournal.clear()
        } catch (rollbackError) {
          console.error(
            'wob-client.adt-client.change-type-request: Rollback FAILED',
            rollbackError
          )
          // Journal stays in localStorage for manual recovery
        }
      }

      // Throw structured error with journal state for the caller
      const err = new Error(`Change type failed at step: ${journal.state}`)
      err.journal = journal
      err.originalError = error
      throw err
    }
  }

  // Rollback helper: recreate the old twin from journal snapshot.
  // Called when the transaction fails after the twin has been deleted.
  async _rollbackChangeType(journal) {
    const { twinId, snapshot } = journal
    const { dataProperties, outgoing, incoming } = snapshot

    // Recreate twin with old model
    // createRequest expects an Iri-like object with .value
    const oldTypeIri = { value: this._modelToIri(journal.oldModel) }
    await this.createRequest(oldTypeIri, twinId)

    // Restore data properties
    const patchBody = Object.entries(dataProperties).map(([key, value]) => ({
      op: 'add',
      path: '/' + key,
      value,
    }))
    if (patchBody.length > 0) {
      await this.updateTwinRequest(twinId, patchBody)
    }

    // Restore relationships (sequentially to avoid 429 rate limiting)
    const restoreOutFns = outgoing.map(
      (r) => () =>
        withRetry(() =>
          this.addRelationshipRequest(
            twinId,
            r['$targetId'],
            r['$relationshipName']
          )
        )
    )
    await sequential(restoreOutFns)

    const restoreInFns = incoming.map(
      (r) => () =>
        withRetry(() =>
          this.addRelationshipRequest(
            r['$sourceId'],
            twinId,
            r['$relationshipName']
          )
        )
    )
    await sequential(restoreInFns)

    // Refresh cache with restored twin
    const restoredTwin = await this.readTwinRequest(twinId)
    const jsonlds = await adtParser(restoredTwin)
    jsonlds.forEach(([jsonld]) => {
      dispatchEvent(new CustomEvent(IndividualRefreshEvent, { detail: jsonld }))
    })
  }

  // Dispatches IndividualRefreshEvent for each member in batches,
  // yielding the event loop (setTimeout(0)) between batches to keep the UI responsive.
  _dispatchMembersBatched(members) {
    const MEMBER_BATCH_SIZE = 10
    let offset = 0
    this._pendingBatches++
    const processNextBatch = () => {
      // Only the synchronous span is charged to `dispatch` — the yields
      // between batches are deliberate idle time, not cost. Each dispatch
      // synchronously drives RecCache.refresh and the selector cascade, so
      // this number is the real per-individual ingest cost.
      const tBatch = performance.now()
      const end = Math.min(offset + MEMBER_BATCH_SIZE, members.length)
      while (offset < end) {
        dispatchEvent(
          new CustomEvent(IndividualRefreshEvent, { detail: members[offset] })
        )
        this.actualIndividualsDispatched++
        if (this._timing) this._timing.individuals++
        offset++
      }
      if (this._timing) this._timing.dispatch += performance.now() - tBatch
      if (offset < members.length) {
        setTimeout(processNextBatch, 0)
      } else {
        this._pendingBatches--
        this._maybeLogTimingSummary()
      }
    }
    processNextBatch()
  }

  // Report per-phase sync timing once the read cycle has finished AND every
  // batched dispatch has drained. Phases are disjoint and sum to the cost
  // this client owes; wall-clock will exceed the sum by the yields between
  // batches plus any debounced work in vue-wob-cache that we don't control.
  _maybeLogTimingSummary() {
    if (!this._syncCompleted || this._pendingBatches > 0 || !this._timing) {
      return
    }
    const t = this._timing
    const wall = performance.now() - t.t0
    const n = t.individuals || 0
    // filter/parse/dispatch are measured directly and are mutually exclusive.
    // Everything else is time this client was not executing: worker
    // round-trips, plus whatever else held the main thread. Deriving it by
    // subtraction keeps the four numbers adding up to wall exactly — an
    // earlier version timed the worker gap directly and double-counted
    // dispatch, because the next request is posted before batches drain.
    const main = t.filter + t.parse + t.dispatch
    const waiting = Math.max(0, wall - main)
    const ms = (v) => `${v.toFixed(0)}ms`
    const pct = (v) => `${((v / wall) * 100).toFixed(0)}%`.padStart(4)
    console.log(
      [
        'ADT sync timing',
        `  wall        ${ms(wall)}  (${(wall / 1000).toFixed(1)}s)`,
        `  ├ filter    ${ms(t.filter)} ${pct(t.filter)}  etag change detection`,
        `  ├ parse     ${ms(t.parse)} ${pct(t.parse)}  adtParser -> JSON-LD`,
        `  ├ dispatch  ${ms(t.dispatch)} ${pct(
          t.dispatch
        )}  RecCache.refresh cascade`,
        `  └ waiting   ${ms(waiting)} ${pct(waiting)}  worker round-trips (${
          t.twinPages
        } twin + ${t.relPages} relationship pages, ${
          t.relContinuations
        } relationship continuations, ${t.posts} requests)`,
        `  ---`,
        `  ${n} individuals dispatched, ${
          n ? (t.dispatch / n).toFixed(2) : '0'
        }ms each in dispatch, ${n ? (t.parse / n).toFixed(2) : '0'}ms in parse`,
        `  ${t.skipped} skipped by etagMap`,
      ].join('\n')
    )
    this._timing = null
    this._syncCompleted = false
  }

  // Convert a DTDL model identifier back to an ontology IRI.
  // Inverse of toDTDL. E.g. "dtmi:org:w3id:rec:Room;1" -> "https://w3id.org/rec#Room"
  _modelToIri(model) {
    const DTMI_TO_NS = {
      'dtmi:org:brickschema:schema:Brick':
        'https://brickschema.org/schema/Brick#',
      'dtmi:org:w3id:rec': 'https://w3id.org/rec#',
      'dtmi:com:webofbuildings:ontology':
        'https://webofbuildings.com/ontology/',
    }
    // Strip version suffix (;1)
    const withoutVersion = model.split(';')[0]
    for (const [prefix, ns] of Object.entries(DTMI_TO_NS)) {
      if (withoutVersion.startsWith(prefix + ':')) {
        const name = withoutVersion
          .substring(prefix.length + 1)
          .replace('_', '.')
        return ns + name
      }
    }
    throw new Error(`Cannot convert DTDL model to IRI: ${model}`)
  }

  // Check for and recover from an incomplete change-type transaction.
  // Called at app startup after authentication.
  // Returns a result object describing what happened, or null if no recovery was needed.
  async recoverChangeTypeTransaction() {
    const journal = ChangeTypeJournal.read()
    if (!journal) return null

    console.log(
      'wob-client.adt-client.recover-change-type: Found journal',
      journal
    )

    try {
      switch (journal.state) {
        case 'started':
          // Nothing was mutated yet — safe to discard
          console.log(
            'wob-client.adt-client.recover-change-type: No mutations occurred, clearing journal'
          )
          ChangeTypeJournal.clear()
          return {
            recovered: true,
            action: 'discarded',
            message:
              'Previous change-type transaction had not started mutations. Cleared.',
            journal,
          }

        case 'rels-deleted':
          // Relationships were deleted but twin still exists.
          // Restore relationships to original state.
          console.log(
            'wob-client.adt-client.recover-change-type: Restoring relationships'
          )
          await this._restoreRelationships(journal)
          ChangeTypeJournal.clear()
          return {
            recovered: true,
            action: 'restored-rels',
            message: `Restored relationships for twin ${journal.twinId} after interrupted type change.`,
            journal,
          }

        case 'deleted':
          // Twin was deleted — must recreate with OLD type (rollback)
          console.log(
            'wob-client.adt-client.recover-change-type: Rolling back — recreating with old type'
          )
          await this._rollbackChangeType(journal)
          ChangeTypeJournal.clear()
          return {
            recovered: true,
            action: 'rolled-back',
            message: `Rolled back type change for twin ${journal.twinId}. Recreated with original type.`,
            journal,
          }

        case 'created':
          // New twin exists but properties not yet restored — continue forward
          console.log(
            'wob-client.adt-client.recover-change-type: Continuing — patching properties and restoring relationships'
          )
          await this._continueChangeType(journal, 'created')
          ChangeTypeJournal.clear()
          return {
            recovered: true,
            action: 'continued',
            message: `Completed interrupted type change for twin ${journal.twinId}. Properties and relationships restored.`,
            journal,
          }

        case 'patched':
          // Properties restored, relationships still missing — continue forward
          console.log(
            'wob-client.adt-client.recover-change-type: Continuing — restoring relationships'
          )
          await this._continueChangeType(journal, 'patched')
          ChangeTypeJournal.clear()
          return {
            recovered: true,
            action: 'continued',
            message: `Completed interrupted type change for twin ${journal.twinId}. Relationships restored.`,
            journal,
          }

        case 'completed':
        case 'rolled-back':
          // Already done — just clean up
          ChangeTypeJournal.clear()
          return {
            recovered: true,
            action: 'cleaned-up',
            message:
              'Previous change-type transaction was already complete. Cleaned up journal.',
            journal,
          }

        default:
          console.error(
            'wob-client.adt-client.recover-change-type: Unknown journal state',
            journal.state
          )
          return {
            recovered: false,
            action: 'unknown-state',
            message: `Unknown journal state: ${journal.state}. Manual recovery may be needed.`,
            journal,
          }
      }
    } catch (error) {
      console.error(
        'wob-client.adt-client.recover-change-type: Recovery FAILED',
        error
      )
      // Journal stays in localStorage for manual investigation
      return {
        recovered: false,
        action: 'failed',
        message: `Recovery failed for twin ${journal.twinId} at state ${journal.state}. Journal preserved in localStorage for manual recovery.`,
        journal,
        error: error.message,
      }
    }
  }

  // Restore relationships from snapshot (used when rels were deleted but twin still exists).
  async _restoreRelationships(journal) {
    const { twinId, snapshot } = journal
    const { outgoing, incoming } = snapshot

    const restoreOutFns = outgoing.map(
      (r) => () =>
        withRetry(() =>
          this.addRelationshipRequest(
            twinId,
            r['$targetId'],
            r['$relationshipName']
          )
        )
    )
    await sequential(restoreOutFns)

    const restoreInFns = incoming.map(
      (r) => () =>
        withRetry(() =>
          this.addRelationshipRequest(
            r['$sourceId'],
            twinId,
            r['$relationshipName']
          )
        )
    )
    await sequential(restoreInFns)

    // Refresh cache
    const twin = await this.readTwinRequest(twinId)
    const jsonlds = await adtParser(twin)
    jsonlds.forEach(([jsonld]) => {
      dispatchEvent(new CustomEvent(IndividualRefreshEvent, { detail: jsonld }))
    })
  }

  // Continue a forward change-type transaction from the given state.
  async _continueChangeType(journal, fromState) {
    const { twinId, snapshot } = journal
    const { dataProperties } = snapshot

    if (fromState === 'created') {
      // Patch data properties
      const patchBody = Object.entries(dataProperties).map(([key, value]) => ({
        op: 'add',
        path: '/' + key,
        value,
      }))
      if (patchBody.length > 0) {
        await this.updateTwinRequest(twinId, patchBody)
      }
    }

    // Restore relationships (both created and patched states need this)
    await this._restoreRelationships(journal)
  }

  // Clear change detection from memory and drop any legacy localStorage copy.
  // The IndexedDB copy lives beside the cache and is cleared with it.
  // Must be called whenever the RecCache is cleared (login/logout/environment switch)
  // to prevent stale entries from suppressing twin dispatch.
  clearEtagMap() {
    this.etagMap = new Map()
    try {
      localStorage.removeItem(ETAG_MAP_STORAGE_KEY)
    } catch (e) {
      // non-fatal
    }
  }

  // Forget cached change-detection state for the given twin id(s).
  // The next sync will re-read those twins from ADT regardless of whether
  // their etag and relationships changed. Takes effect in memory and is
  // persisted with the cache's next save; should that save not happen, the
  // retain step in startSync() and the etag comparison still force a re-read
  // where it matters. Accepts a single id or an array; empty input is a no-op.
  forgetChangeDetection(ids) {
    if (!ids) return
    const list = Array.isArray(ids) ? ids : [ids]
    for (const id of list) this.etagMap.delete(id)
  }

  // Keep only the change-detection entries whose twin is actually present in
  // the hydrated cache; drop the rest. Guarantees the invariant the ETag
  // filter depends on: skipping a twin is only safe when the cache already
  // holds it. `ids` is any iterable of twin ids ($dtId, which is the same
  // value as RecIndividual.id.value for ADT twins).
  retainChangeDetection(ids) {
    const keep = ids instanceof Set ? ids : new Set(ids)
    let removed = 0
    for (const twinId of [...this.etagMap.keys()]) {
      if (!keep.has(twinId)) {
        this.etagMap.delete(twinId)
        removed++
      }
    }
    console.log(
      `ADT: change detection retained ${this.etagMap.size} entries, dropped ${removed} not present in the cache`
    )
  }

  // Read the change-detection map the previous release kept in localStorage.
  // Only the first load after the move to IndexedDB finds one; the cache
  // replaces it via restoreChangeDetection() whenever IndexedDB holds a copy.
  _loadLegacyEtagMap() {
    // Clean up old etagMap keys from previous versions and legacy sessionStorage
    this._cleanupOldEtagMaps()

    try {
      const raw = localStorage.getItem(ETAG_MAP_STORAGE_KEY)
      if (raw) {
        const entries = JSON.parse(raw)
        console.log(
          `ADT: Read legacy etagMap from localStorage (${entries.length} entries, format v${CACHE_FORMAT_VERSION})`
        )
        return new Map(entries)
      }
    } catch (e) {
      console.warn('ADT: Failed to read legacy etagMap from localStorage', e)
    }
    return new Map()
  }

  // Change-detection state, handed to the cache to persist beside the
  // individuals it describes. Opaque to the cache.
  getChangeDetectionState() {
    return Array.from(this.etagMap.entries())
  }

  // Restore the state the cache persisted with the individuals it has just
  // hydrated. Replaces whatever was read from the legacy localStorage copy.
  restoreChangeDetection(entries) {
    this.etagMap = new Map(entries)
    console.log(
      `ADT: Restored change detection from IndexedDB (${this.etagMap.size} entries)`
    )
  }

  // The cache has persisted a copy, so the legacy localStorage one is now
  // redundant. Removing it hands ~4MB back to the connection profile and the
  // change-type journal, which share that origin's ~5MB.
  changeDetectionPersisted() {
    if (this._legacyEtagMapRemoved) return
    try {
      localStorage.removeItem(ETAG_MAP_STORAGE_KEY)
      this._legacyEtagMapRemoved = true
    } catch (e) {
      // non-fatal
    }
  }

  // Remove etagMap entries from previous versions and legacy sessionStorage.
  // Called once on load to prevent localStorage bloat across deployments.
  _cleanupOldEtagMaps() {
    try {
      // Remove legacy sessionStorage entry
      sessionStorage.removeItem('etagMap')

      // Remove old versioned keys from localStorage
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i)
        if (
          key &&
          key.startsWith('etagMap_v') &&
          key !== ETAG_MAP_STORAGE_KEY
        ) {
          localStorage.removeItem(key)
        }
      }
    } catch (e) {
      // non-fatal
    }
  }
}

export { AdtClient, ChangeTypeJournal }
